import { Assignment, Info, DownloadForOffline } from "@mui/icons-material";
import { Dialog, DialogTitle, Typography, DialogContent, Grid, DialogActions, Button } from "@mui/material";
import { FC, useCallback, useMemo, useState } from "react";
import { DataTableColumn, StandardListViewV2 } from "../../../Components/CoreLib/library";
import { triaxReferenceNumberColumnKeySuffixes } from '../../../constants/triaxReferenceNumberColumnKeySuffixes';
import { QuoteAffectingChangesDto, QuoteDto, QueryResultsDto } from "../../../dtos";
import { useGetQuoteAffectedQuery } from "../../../store/generated/generatedApi";
import { exportToCsv } from "../../../util/fileDownloadHelper";
import { formatDate } from "../../../util";

export interface IQuotesAffectedByChangeListViewDialog {
    onClose: () => void;
    onConfirm: () => void;
    open: boolean;
    changes: QuoteAffectingChangesDto;
}

export const QuotesAffectedByChangeListViewDialog: FC<IQuotesAffectedByChangeListViewDialog> = (props) => {
    const { onClose, onConfirm, open, changes } = props;
    const [queryResults, setQueryResults] = useState<QueryResultsDto<QuoteDto> | undefined>(undefined);

    const getQueryParameters = useCallback(() => {
        return JSON.stringify(changes);
    }, [changes]);

    const tableColumns: DataTableColumn<QuoteDto>[] = useMemo(() => {
        let columns = [
            { key: 'quoteNumber', label: 'Quote #', sortKey: 'QUOTE_NUMBER', },
            { key: 'projectName', label: 'Project Name', sortKey: 'PROJECT_NAME' },
            { key: 'status', label: 'Status', sortKey: 'STATUS' },
            { key: 'quoteDate', label: 'Quote Date', sortKey: 'QUOTE_DATE', fieldMapper: (row) => (row.quoteDate ? formatDate(row.quoteDate) : '') },
            { key: 'effectiveOn', label: 'Effective On Date', sortKey: 'EFFECTIVE_ON_DATE', fieldMapper: (row) => (row.effectiveOn ? formatDate(row.effectiveOn) : '') },
            { key: 'expirationDate', label: 'Current Expiration Date', sortKey: 'EXPIRATION_DATE', fieldMapper: (row) => (row.expirationDate ? formatDate(row.expirationDate) : '') },
            {
                key: 'newExpirationDate', label: 'New Expiration Date', sortKey: 'NEW_EXPIRATION_DATE', fieldMapper: (row) => (row.newExpirationDate ?
                    <Grid container direction='row' alignItems='center' wrap='nowrap' spacing={1}>
                        <Grid item>
                            <Typography>{formatDate(row.newExpirationDate)}</Typography>
                        </Grid>
                        <Grid item>
                            <Info color='error' />
                        </Grid>
                    </Grid>
                    : '')
            }

        ] as DataTableColumn<QuoteDto>[];

        return columns;
    }, []);

    const handleExportToCsv = useCallback(() => {
        if (!queryResults?.pageResults) {
            return;
        }

        const quotes = queryResults.pageResults;
        const headers = ['Quote #', 'Project Name', 'Status', 'Quote Date', 'Effective On Date', 'Current Expiration Date', 'New Expiration Date'];
        
        // Transform data to match CSV columns - use array format to preserve order
        const csvData = quotes.map((quote: QuoteDto) => [
            quote.quoteNumber ?? '',
            quote.projectName ?? '',
            quote.status ?? '',
            quote.quoteDate ? formatDate(quote.quoteDate) : '',
            quote.effectiveOn ? formatDate(quote.effectiveOn) : '',
            quote.expirationDate ? formatDate(quote.expirationDate) : '',
            quote.newExpirationDate ? formatDate(quote.newExpirationDate) : ''
        ]);

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const fileName = `AffectedQuotes-${timestamp}.csv`;

        exportToCsv(csvData, headers, fileName);
    }, [queryResults]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg' PaperProps={{ sx: { width: '100%' } }}>
            <DialogTitle>
                <Typography variant='h2'>
                    {<Assignment />} Affected Quotes
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography variant='h6' mt={2}>The following quotes will be affected by the Material Price change.</Typography>
                <StandardListViewV2
                    referenceNumberColumnKeySuffixes={triaxReferenceNumberColumnKeySuffixes}
                    enableMultiSort
                    headerIcon={<></>}
                    headerTitle={''}
                    disableSelection
                    hideMenu
                    hideSearch
                    breadCrumbs={[]}
                    isPaginationVisible={false}
                    getDataQuery={useGetQuoteAffectedQuery}
                    additionalQueryParameters={{ serializedDto: getQueryParameters() }}
                    isShowActiveToggleVisible={false}
                    tableColumns={tableColumns}
                    entityNameSingular='Quote'
                    permissionName='quote'
                    hideAddButtonDivider
                    onQueryResultsChange={setQueryResults}
                    onNoDataLoaded={() => {
                        onConfirm();
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
                <Grid container direction='row' justifyContent='end'>
                    <Grid item>
                        <Button 
                            variant='outlined' 
                            style={{ boxShadow: 'none', marginRight: '15px' }} 
                            onClick={handleExportToCsv}
                            disabled={!queryResults?.pageResults || queryResults.pageResults.length === 0}
                            startIcon={<DownloadForOffline />}
                        >
                            Export to CSV
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant='outlined' style={{ boxShadow: 'none', marginRight: '15px' }} onClick={onClose}>
                            Close
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant='contained' style={{ boxShadow: 'none', marginRight: '15px' }} onClick={onConfirm}>
                            Confirm
                        </Button>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
}